import { Color3 } from '@babylonjs/core/Maths/math.color';

/**
 * Pure colour shaping for the room accent's own transition — the owner's own shape: ease to
 * white over one phase, then from white to the new colour over a second phase, so a trim
 * recolour reads as *changing* rather than *changed*. Kept separate from `SceneRoot`'s own
 * Babylon plumbing (the accent material, the render loop) the same way `AssistantLightAnimation`
 * is kept separate from the light it drives — a plain function of numbers and colours is what
 * this project's test suite can actually exercise directly, with no renderer involved.
 *
 * A snap straight from `from` to `to` would still be a truthful reflection of server state (the
 * mutation already happened by the time this runs at all), but the whole point here is that a
 * colour change is *feedback*, not merely *masking latency it might later be fast enough not to
 * need* — see CLAUDE.md's own framing, repeated in plan/ for this milestone.
 */

/** One step of the accent transition. */
export interface AccentColorStep {
  /** The colour to paint this instant. */
  readonly color: Color3;
  /** Whether the transition has reached `to` and needs no further ticking. */
  readonly done: boolean;
}

const WHITE = Color3.White();

/** Smoothstep — eases in and out of each phase rather than moving at a constant rate, so
 *  neither phase starts or ends with a visible kink. */
function ease(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped * clamped * (3 - 2 * clamped);
}

/**
 * The colour to show `elapsedMs` after a transition from `from` to `to` began, given a
 * `phaseMs`-long first half (`from` -> white) and an equally long second half (white -> `to`).
 *
 * `from`/`to` are read, never mutated — every `Color3` this returns is a fresh instance
 * (`Color3.Lerp` already allocates one), so a caller holding onto an old step's `color` is safe
 * from a later call quietly changing it out from under them.
 */
export function accentColorAt(
  elapsedMs: number,
  from: Color3,
  to: Color3,
  phaseMs: number,
): AccentColorStep {
  if (elapsedMs >= phaseMs * 2) {
    return { color: to, done: true };
  }

  if (elapsedMs < phaseMs) {
    return { color: Color3.Lerp(from, WHITE, ease(elapsedMs / phaseMs)), done: false };
  }

  return { color: Color3.Lerp(WHITE, to, ease((elapsedMs - phaseMs) / phaseMs)), done: false };
}
